import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ThumbsDown, ThumbsUp } from "lucide-react";

import { useLanguage } from "../../context/languageContext";
import styles from "./summary.module.scss";

/**
 * What readers made of THIS summary, on the screen where it can be acted on.
 *
 * Feedback existed only as a flat list, so an editor opening a summary had no
 * sign that nine readers liked it and three did not — the verdict lived on
 * another page, findable only by scanning for the title.
 *
 * A tally rather than the rows: the counts are what decide whether to reread a
 * chapter, and the rows are one click away, filtered to this summary.
 *
 * Renders NOTHING when there is no feedback. A summary nobody has rated should
 * not carry an empty panel saying so — on most summaries that is the normal
 * case and it would be noise on every edit.
 */
const FeedbackTally = ({ summaryId }) => {
  const { adminInfo } = useLanguage();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!summaryId || !adminInfo?.token) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_ROOT}/api/admin/summary-feedback/stats/${summaryId}`,
          { headers: { Authorization: `Bearer ${adminInfo.token}` } }
        );
        if (!cancelled && data?.status) setStats(data.data);
      } catch (_) {
        // Silent: a summary's feedback count is not worth a toast over the
        // form someone came here to edit.
      }
    })();

    return () => { cancelled = true; };
  }, [summaryId, adminInfo?.token]);

  if (!stats?.total) return null;

  return (
    <div className={styles.feedbackTally}>
      <Link to={`/feedback?summaryId=${summaryId}`} title="Read what they said">
        {stats.total} {stats.total === 1 ? "rating" : "ratings"}
      </Link>
      <span className="text-success d-inline-flex align-items-center gap-1">
        <ThumbsUp size={14} /> {stats.positive}
      </span>
      <span className="text-danger d-inline-flex align-items-center gap-1">
        <ThumbsDown size={14} /> {stats.negative}
      </span>
      {/* The reasons, most-given first. Paired with the rating that produced
          them, because "Too short" from someone who liked it means something
          else. */}
      {stats.tags.slice(0, 4).map((t) => (
        <span
          key={`${t.tag}-${t.rating}`}
          className={`${styles.feedbackTag} ${t.rating === "negative" ? styles.feedbackTagNegative : ""}`}
        >
          {t.tag} ×{t.count}
        </span>
      ))}
    </div>
  );
};

export default FeedbackTally;
